import React, { useState, useEffect, useRef  } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";

// Use the environment variable for the API base URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CreateNewOrderCycle() {
  const [description, setDescription] = useState("")
  const [selectedLocation, setSelectedLocation] = useState("")
  const [locations, setLocations] = useState([])
  const [orderCycleCode, setOrderCycleCode] = useState("")
  const [createdDate, setCreatedDate] = useState("")
  const [createdBy, setCreatedBy] = useState("")
  const [orderDate, setOrderDate] = useState("")
  const [deliveryDate, setDeliveryDate] = useState("")
  const [loading, setLoading] = useState(false)
  const [latestBatchProducts, setLatestBatchProducts] = useState([])
  const [currentUser, setCurrentUser] = useState("")
  const hasFetched = useRef(false)
  const [productsWithZeroPrice, setProductsWithZeroPrice] = useState([])
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [validProducts, setValidProducts] = useState([]);

  // Helper function to safely parse price
  const parsePrice = (priceData) => {
    if (!priceData) return 0

    // Handle different price data structures
    if (typeof priceData === "number") return priceData
    if (typeof priceData === "string") return Number.parseFloat(priceData) || 0
    if (priceData.$numberDecimal) return Number.parseFloat(priceData.$numberDecimal) || 0
    if (priceData.value) return Number.parseFloat(priceData.value) || 0

    return 0
  }

  // Inside the component
  const getLocationName = () => {
    const selected = locations.find((location) => location._id === selectedLocation)
    return selected ? selected.location_name : "Unknown Location"
  }

  // Fetch locations and current user
  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token missing. Please log in.")
        return
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        const userData = response.data
        setCurrentUser(userData)

        // Set Created By field
        setCreatedBy(userData.username)

        // Fetch locations and products based on role
        if (userData.role === "Main Admin") {
          fetchAllLocations()
        } else if (userData.role === "Company Admin" || "Outlet User") {
          fetchFilteredLocations()
        }
      } catch (error) {
        console.error("Error fetching user data:", error)
        toast.error("Failed to fetch user data.")
      }
    }

    const fetchAllLocations = async () => {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setLocations(response.data.locations)
    }

    const fetchFilteredLocations = async () => {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_BASE_URL}/products/filtered-locations`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setLocations(response.data.locations)
    }

    fetchCurrentUser()
  }, [])

  useEffect(() => {
    if (!currentUser) return
    if (hasFetched.current) return

    hasFetched.current = true

    const generateCycleCode = async () => {
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")

      try {
        // Fetch organization prefix from the new endpoint
        const token = localStorage.getItem("token")
        const response = await axios.get(
          `${API_BASE_URL}/organizations/prefix`, // New endpoint to fetch only the prefix
          {
            headers: {
              Authorization: `Bearer ${token}`, // Send the token in the header
            },
          },
        )

        const organizationPrefix = response.data.organization_prefix // Organization prefix returned from the backend

        const latestCycleNumber = await axios.get(`${API_BASE_URL}/orders/order-cycle/latest-cycle-number`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        const cycleNumber = latestCycleNumber.data.nextOrderCycleNumber || 1

        const code = `${organizationPrefix}-OC-${year}-${month}-${cycleNumber}`
        setOrderCycleCode(code)
        setCreatedDate(new Date().toLocaleString())
        setCreatedBy(currentUser?.username || "Unknown User") // Replace with the logged-in user
      } catch (error) {
        console.error("Error fetching batch number:", error)
        toast.error("Failed to generate batch number.")
      }
    }

    if (currentUser) generateCycleCode()
  }, [currentUser]) // Dependency array now includes currentUser

  const handleCreateOrderCycle = async () => {
    if (!description || !selectedLocation) {
      toast.error("All fields are required")
      return
    }

    setLoading(true)

    try {
      const token = localStorage.getItem("token")

      if (!token) {
        toast.error("User is not authenticated")
        setLoading(false)
        return
      }

      const response = await axios.get(`${API_BASE_URL}/latestStock/getStockByLocationNew/${selectedLocation}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const latestStocks = response.data || []

      if (latestStocks.length === 0) {
        toast.error("No products available for this location.")
        setLoading(false)
        return
      }

      const priceSafeStocks = latestStocks.map(product => ({
        ...product,
        safePrice: parsePrice(product.latest_price)
      }));

      //zero price detection with debugging
      const productsWithZeroPrice = priceSafeStocks.filter(p => p.safePrice === 0);
      const validProducts = priceSafeStocks.filter(p => p.safePrice > 0);

      setValidProducts(validProducts);


      setLatestBatchProducts(latestStocks) // Save full stock for reuse

      if (productsWithZeroPrice.length > 0) {
        setProductsWithZeroPrice(productsWithZeroPrice)
        setShowConfirmModal(true)
        setLoading(false)
        return
      }

      // If no zero price products, proceed directly
      await proceedWithOrderCycle(validProducts);
    } catch (error) {
      console.error("Error creating order cycle:", error)
      toast.error(error.message || "Failed to create order cycle")
    } finally {
      setLoading(false)
    }
  }

  const proceedWithOrderCycle = async (validProducts) => {
    setLoading(true)
    try {
        if (validProducts.length === 0) {
            toast.error("No valid products left to create order cycle.")
            setLoading(false)
            return
        }

        const payload = {
            orderCycleCode,
            description,
            location: selectedLocation,
            products: validProducts.map((product) => ({
                product: product.product_id,
                product_name: product.product_name,
                aggregatedQuantity: product.updated_stock_qty,
                unit: product.unit || "N/A",
                latestPrice: product.safePrice.toFixed(2),  // Use safePrice here
            })),
            orderDate,
            deliveryDate,
        }

        const token = localStorage.getItem("token")

        const orderCycleResponse = await axios.post(`${API_BASE_URL}/orders/order-cycle/createNewOrderCycle`, payload, {
            headers: { Authorization: `Bearer ${token}` },
        })

        const newOrderCycle = orderCycleResponse.data

        toast.success("Order Cycle created successfully")
        setOrderCycleCode(newOrderCycle.orderCycleCode)
        setCreatedDate(newOrderCycle.createdDate)
        setDescription("")
        setSelectedLocation("")
        fetchLatestOrderCycleCode()
    } catch (error) {
        console.error("Error creating order cycle:", error)
        toast.error(error.message || "Failed to create order cycle")
    } finally {
        setLoading(false)
    }
  }

  const fetchLatestOrderCycleCode = async () => {
    try {
      const token = localStorage.getItem("token")
      const response = await axios.get(`${API_BASE_URL}/orders/order-cycle/latest-cycle-number`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const latestCycleNumber = response.data.nextOrderCycleNumber || 1

      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")

      // Fetch organization prefix
      const prefixResponse = await axios.get(`${API_BASE_URL}/organizations/prefix`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const organizationPrefix = prefixResponse.data.organization_prefix

      const newCode = `${organizationPrefix}-OC-${year}-${month}-${latestCycleNumber}`
      setOrderCycleCode(newCode)
    } catch (error) {
      console.error("Error fetching latest order cycle code:", error)
      toast.error("Failed to fetch new order cycle code.")
    }
  }

  const handleOrderDateChange = (e) => {
    const selectedOrderDate = e.target.value
    setOrderDate(selectedOrderDate)

    // Validate: order date should not exceed delivery date
    if (deliveryDate && selectedOrderDate > deliveryDate) {
      console.error("Order date cannot be later than the delivery date.")
      toast.error("Order date cannot be later than the delivery date.")
      setOrderDate("")
    }
  }

  

  return (
    <div className="container mx-auto p-8">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add Order Cycle</h2>

        <div className="grid grid-cols-3 gap-8">
          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Order Cycle Code</label>
            <input type="text" className="w-full p-3 border rounded-lg" value={orderCycleCode} readOnly />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Created Date & Time</label>
            <input type="text" className="w-full p-3 border rounded-lg" value={createdDate} readOnly />
          </div>

          <div className="mb-4">
            <label className="block text-gray-700 mb-2">Created By</label>
            <input type="text" className="w-full p-3 border rounded-lg" value={createdBy} readOnly />
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Description</label>
          <textarea
            className="w-full p-3 border rounded-lg"
            placeholder="Enter order cycle description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="">
            <label className="block text-gray-700 mb-2">Location</label>
            <select
              className="w-full p-3 border rounded-lg"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="">Select Location</option>
              {locations.map((location) => (
                <option key={location._id} value={location._id}>
                  {location.location_name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Delivery Date</label>
            <input
              type="date"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-gray-700 mb-2">Order Date</label>
            <input
              type="date"
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={orderDate}
              onChange={handleOrderDateChange}
              disabled={!deliveryDate}
            />
          </div>
        </div>

        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-red-600 mb-4">Products with Zero Price Detected!</h3>
              <p className="text-gray-700 mb-4">
                The following products have price 0 and will be excluded from the order cycle:
              </p>
              <div className="max-h-40 overflow-y-auto mb-4">
                <ul className="list-disc list-inside space-y-1">
                  {productsWithZeroPrice.map((product) => (
                    <li key={product.product_id} className="text-sm text-gray-600">
                      {product.product_name} - Price: {parsePrice(product.latest_price)}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowConfirmModal(false)
                    proceedWithOrderCycle(validProducts)
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Proceed Anyway
                </button>
                <button
                  onClick={() => {
                    setShowConfirmModal(false)
                    toast.info("Order cycle creation cancelled.")
                    setLoading(false)
                  }}
                  className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-right">
          <button
            onClick={handleCreateOrderCycle}
            disabled={loading}
            className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300 disabled:opacity-50"
          >
            {loading ? "Loading..." : "Create Order Cycle"}
          </button>
        </div>

        {latestBatchProducts.length > 0 && (
          <div className="mt-8">
            <h3 className="text-xl font-bold mb-4">Latest Products for {getLocationName()} :</h3>
            <div style={{ height: 400, width: "100%" }}>
              <DataGrid
                rows={latestBatchProducts.map((product, index) => ({
                  id: product?.product?._id || index,
                  product_code: product?.product_code || "N/A",
                  product_image: product?.product_image || "",
                  product_name: product?.product_name || "Unknown Product",
                  aggregatedQuantity: product?.updated_stock_qty || 0,
                  unit: product?.unit || "N/A",
                  latestPrice: parsePrice(product?.latest_price),
                }))}
                columns={[
                  {
                    field: "product_code",
                    headerName: "Product Code",
                    width: 150,
                  },
                  {
                    field: "product_image",
                    headerName: "Image",
                    width: 150,
                    renderCell: (params) =>
                      params.row.product_image ? (
                        <img
                          src={`${process.env.REACT_APP_IMAGE_BASE_URL}${params.row.product_image}`}
                          alt="Product"
                          className="h-16 w-16 object-cover rounded-md"
                        />
                      ) : (
                        "No Image"
                      ),
                  },
                  {
                    field: "product_name",
                    headerName: "Product Name",
                    width: 200,
                  },
                  {
                    field: "aggregatedQuantity",
                    headerName: "Stock Quantity",
                    width: 150,
                    type: "number",
                  },
                  {
                    field: "unit",
                    headerName: "Unit",
                    width: 150,
                    type: "number",
                    headerAlign: "center",
                    align: "center",
                  },
                  {
                    field: "latestPrice",
                    headerName: "Price",
                    width: 150,
                    headerAlign: "right",
                    align: "right",
                    renderCell: (params) => {
                      const price = Number.parseFloat(params.value)
                      const isZeroPrice = price === 0
                      return (
                        <span className={isZeroPrice ? "text-red-600 font-bold" : ""}>
                          {isNaN(price) ? "LKR 0.00" : `LKR ${price.toFixed(2)}`}
                        </span>
                      )
                    },
                  },
                ]}
                pageSize={5}
                rowsPerPageOptions={[5, 10, 20]}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateNewOrderCycle;
